import React, { useState, useEffect } from 'react';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import jsPDF from 'jspdf';
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.mjs`;

const Tool2Page = () => {
  // PDF Merge
  const [mergeFiles, setMergeFiles] = useState([]); // Array of File
  const [mergeStatus, setMergeStatus] = useState("");
  const handleMergeFiles = (e) => {
    setMergeFiles(Array.from(e.target.files));
    setMergeStatus("");
  };
  const handleMergePdfs = async () => {
    if (!mergeFiles.length || mergeFiles.length < 2) return;
    setMergeStatus("Merging PDFs...");
    try {
      const mergedPdf = await PDFDocument.create();
      for (const file of mergeFiles) {
        const arr = await file.arrayBuffer();
        const pdf = await PDFDocument.load(arr);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach(page => mergedPdf.addPage(page));
      }
      const mergedBytes = await mergedPdf.save();
      saveAs(new Blob([mergedBytes], { type: 'application/pdf' }), 'merged.pdf');
      setMergeStatus("PDFs merged successfully. Downloaded merged PDF.");
    } catch (err) {
      setMergeStatus("Failed to merge PDFs. " + (err.message || ""));
    }
  };
  // State for tabs
  // Tab system removed; all tools always visible
  // Image to PDF
  const [images, setImages] = useState([]); // {file, url}
  const [imgToPdfStatus, setImgToPdfStatus] = useState("");
  // PDF to Image
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfImages, setPdfImages] = useState([]); // {url, name}
  const [pdfToImgStatus, setPdfToImgStatus] = useState("");
  // PDF type conversion
  const [pdfConvFile, setPdfConvFile] = useState(null);
  const [convType, setConvType] = useState('pdfa');
  const [convStatus, setConvStatus] = useState('');

  // PDF Compress
  const [compressPdfFile, setCompressPdfFile] = useState(null);
  const [compressStatus, setCompressStatus] = useState('');
  const [compressEstimate, setCompressEstimate] = useState(null); // {size: number, unit: 'KB'|'MB'}
  const [compressQuality, setCompressQuality] = useState(60); // JPEG quality (10-100)
  // Word to PDF
  const [wordFile, setWordFile] = useState(null);
  const [wordToPdfStatus, setWordToPdfStatus] = useState('');
  // PDF to Word
  const [pdfToWordFile, setPdfToWordFile] = useState(null);
  const [pdfToWordStatus, setPdfToWordStatus] = useState('');
  // Word to PDF handler
  const handleWordFile = (e) => {
    if (!e.target.files[0]) return;
    setWordFile(e.target.files[0]);
    setWordToPdfStatus('');
  };
  const handleWordToPdf = async () => {
    if (!wordFile) return;
    setWordToPdfStatus('Converting...');
    // Convert DOCX to HTML using mammoth
    const arrayBuffer = await wordFile.arrayBuffer();
    const { value: html } = await mammoth.convertToHtml({ arrayBuffer });
    // Render HTML to PDF using jsPDF
    const doc = new jsPDF('p', 'pt', 'a4');
    doc.html(html, {
      callback: function (pdf) {
        pdf.save('word-to-pdf.pdf');
        setWordToPdfStatus('Word file converted to PDF successfully.');
      },
      margin: [20, 20, 20, 20],
      autoPaging: 'text',
      x: 10,
      y: 10,
      width: 570
    });
  };

  // PDF to Word handler (simple text extraction demo)
  const handlePdfToWordFile = (e) => {
    if (!e.target.files[0]) return;
    setPdfToWordFile(e.target.files[0]);
    setPdfToWordStatus('');
  };
  const handlePdfToWord = async () => {
    if (!pdfToWordFile) return;
    setPdfToWordStatus('Converting...');
    const arr = await pdfToWordFile.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arr }).promise;
    let text = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map(item => item.str).join(' ') + '\n\n';
    }
    // Save as .docx using blob (plain text, for demo)
    const blob = new Blob([text], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    saveAs(blob, 'pdf-to-word.docx');
    setPdfToWordStatus('PDF converted to Word successfully. (Note: Only text is extracted, formatting/images not preserved)');
  };

  // Image to PDF handler
  const handleImageFiles = (e) => {
    const files = Array.from(e.target.files);
    setImages(files.map(f => ({ file: f, url: URL.createObjectURL(f) })));
    setImgToPdfStatus("");
  };
  const handleImageToPdf = async () => {
    if (!images.length) return;
    const pdfDoc = await PDFDocument.create();
    for (const imgObj of images) {
      const imgBytes = await imgObj.file.arrayBuffer();
      let pdfImg, dims;
      if (imgObj.file.type === 'image/jpeg' || imgObj.file.type === 'image/jpg') {
        pdfImg = await pdfDoc.embedJpg(imgBytes);
        dims = pdfImg.scale(1);
      } else {
        pdfImg = await pdfDoc.embedPng(imgBytes);
        dims = pdfImg.scale(1);
      }
      const page = pdfDoc.addPage([dims.width, dims.height]);
      page.drawImage(pdfImg, { x: 0, y: 0, width: dims.width, height: dims.height });
    }
    const pdfBytes = await pdfDoc.save();
    saveAs(new Blob([pdfBytes], { type: 'application/pdf' }), 'images-to-pdf.pdf');
    setImgToPdfStatus("Successfully downloaded PDF.");
  };

  // PDF to Image handler
  const handlePdfFile = (e) => {
    if (!e.target.files[0]) return;
    setPdfFile(e.target.files[0]);
    setPdfImages([]);
    setPdfToImgStatus("");
  };
  const handlePdfToImages = async () => {
    if (!pdfFile) return;
    setPdfToImgStatus("Converting PDF to images...");
    const fileReader = new FileReader();
    fileReader.onload = async function() {
      const typedarray = new Uint8Array(this.result);
      const pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
      const imgs = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        await page.render({ canvasContext: context, viewport }).promise;
        const url = canvas.toDataURL('image/png');
        imgs.push({ url, name: `page-${i}.png` });
      }
      setPdfImages(imgs);
      setPdfToImgStatus("Images are ready for download successfully.");
    };
    fileReader.readAsArrayBuffer(pdfFile);
  };

  // PDF type conversion (simple re-save, real PDF/A/X needs backend or advanced lib)
  const handlePdfConvFile = (e) => {
    if (!e.target.files[0]) return;
    setPdfConvFile(e.target.files[0]);
    setConvStatus('');
  };
  const handlePdfTypeConvert = async () => {
    if (!pdfConvFile) return;
    setConvStatus('Converting...');
    // For demo: just re-save as PDF (real PDF/A/X needs backend or advanced lib)
    const arr = await pdfConvFile.arrayBuffer();
    const pdfDoc = await PDFDocument.load(arr);
    const pdfBytes = await pdfDoc.save();
    saveAs(new Blob([pdfBytes], { type: 'application/pdf' }), `converted-${convType}.pdf`);
    setConvStatus('Done! (Note: Real PDF/A/X needs backend or pro library)');
  };

  // Sync with dark mode from Navbar
  const [darkMode, setDarkMode] = useState(() => document.body.classList.contains('dark-mode'));
  useEffect(() => {
    const handler = () => setDarkMode(document.body.classList.contains('dark-mode'));
    window.addEventListener('darkModeChanged', handler);
    return () => window.removeEventListener('darkModeChanged', handler);
  }, []);

  // PDF to Image download selection
  const [downloadSelect, setDownloadSelect] = useState('all');
  const [downloadCustom, setDownloadCustom] = useState('');
  const [pdf2imgDownloadStatus, setPdf2imgDownloadStatus] = useState('');

  return (
    <div className={"tool2page-container" + (darkMode ? " tool2page-dark" : "") }>
      {/* Features section at the top */}
      <div className="tool2page-features" style={{
        background: 'var(--bg-secondary, #f5f6fa)',
        color: 'var(--text-primary, #2d2d2d)',
        borderRadius: 10,
        margin: '0 auto 18px auto',
        padding: '16px 12px 10px 12px',
        maxWidth: 520,
        boxShadow: '0 2px 8px var(--shadow-color, rgba(154,59,59,0.04))'
      }}>
        <h3 className="tool2page-features-title" style={{
          margin: '0 0 8px 0',
          fontSize: 17,
          color: 'var(--accent-color, #9A3B3B)',
          fontWeight: 700,
          letterSpacing: 0.5
        }}>Features</h3>
        <ul className="tool2page-features-list" style={{
          margin: 0,
          paddingLeft: 18,
          fontSize: 15,
          color: 'var(--text-primary, #2d2d2d)'
        }}>
          <li><b>Image to PDF:</b> Convert multiple images into a single PDF file.</li>
          <li><b>PDF to Image:</b> Convert each page of a PDF into separate images (PNG format) and download selected pages.</li>
          <li><b>Compress PDF:</b> Reduce PDF file size by rasterizing pages as images (best for image-heavy PDFs).</li>
          <li><b>Word to PDF:</b> Convert .docx Word documents to PDF format.</li>
          <li><b>PDF to Word:</b> Extract text from PDF and save as a .docx Word file (text only, formatting/images not preserved).</li>
          <li><b>PDF Type Conversion:</b> Save PDF as PDF/A, PDF/X, or standard PDF (demo only).</li>
          <li>Download results with a single click for each tool.</li>
          <li>All operations run fully in your browser—no file uploads required.</li>
          <li>Modern, mobile-friendly and desktop responsive UI.</li>
        </ul>
      </div>
      <div className="tool2page-card" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        {/* <h2 className="tool2page-title">PDF/Image Converter</h2> */}
        {/* All tools shown as separate sections with dashed borders */}
        <div className="tool2page-section tool2page-toolbox">
          <h3>Image to PDF</h3>
          <label className="tool2page-input-label">
            <span className="tool2page-file-btn">Choose Images
              <input type="file" accept="image/*" multiple onChange={handleImageFiles} className="tool2page-input" />
            </span>
            <span className="tool2page-file-name">{images.length > 0 ? images.map(img => img.file.name).join(', ') : 'No file chosen'}</span>
          </label>
          {/* No image preview for Image to PDF */}
          <button className="tool2page-main-btn" disabled={!images.length} onClick={handleImageToPdf}>Convert & Download PDF</button>
                  {/* No status message for Image to PDF as no status state is present */}
          {imgToPdfStatus && <div className="tool2page-status tool2page-status-success">{imgToPdfStatus}</div>}
        </div>

        <div className="tool2page-section tool2page-toolbox">
          <h3>PDF to Image</h3>
          <label className="tool2page-input-label">
            <span className="tool2page-file-btn">Choose PDF
              <input type="file" accept="application/pdf" onChange={handlePdfFile} className="tool2page-input" />
            </span>
            <span className="tool2page-file-name">{pdfFile ? pdfFile.name : 'No file chosen'}</span>
          </label>
          <button className="tool2page-main-btn" disabled={!pdfFile} onClick={handlePdfToImages}>Convert PDF to Images</button>
          {pdfToImgStatus && <div className="tool2page-status tool2page-status-success">{pdfToImgStatus}</div>}
          {pdfImages.length > 0 && (
            <div style={{marginTop: 12}}>
              <div style={{marginBottom: 10}}>
                <label style={{fontWeight: 500, marginRight: 8}}>Download:</label>
                <select value={downloadSelect} onChange={e => setDownloadSelect(e.target.value)} style={{padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', marginRight: 8}}>
                  <option value="all">All Pages</option>
                  <option value="odd">Odd Pages</option>
                  <option value="even">Even Pages</option>
                  <option value="custom">Custom</option>
                </select>
                {downloadSelect === 'custom' && (
                  <input
                    type="text"
                    placeholder="e.g. 1,3,5-7"
                    value={downloadCustom}
                    onChange={e => setDownloadCustom(e.target.value)}
                    style={{padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', width: 120}}
                  />
                )}
              </div>
              <button
                className="tool2page-btn"
                style={{background: '#22c55e', color: '#fff', border: 'none', fontWeight: 600, boxShadow: '0 1px 4px rgba(34,197,94,0.10)', transition: 'background 0.2s', cursor: 'pointer', padding: '8px 18px', borderRadius: 6, fontSize: 15}}
                onMouseOver={e => (e.currentTarget.style.background = '#16a34a')}
                onMouseOut={e => (e.currentTarget.style.background = '#22c55e')}
                onClick={async () => {
                  let selected = [];
                  if (downloadSelect === 'all') {
                    selected = pdfImages;
                  } else if (downloadSelect === 'odd') {
                    selected = pdfImages.filter((_, idx) => (idx+1) % 2 === 1);
                  } else if (downloadSelect === 'even') {
                    selected = pdfImages.filter((_, idx) => (idx+1) % 2 === 0);
                  } else if (downloadSelect === 'custom') {
                    const pages = new Set();
                    downloadCustom.split(',').forEach(part => {
                      if (part.includes('-')) {
                        const [start, end] = part.split('-').map(Number);
                        for (let i = start; i <= end; i++) pages.add(i);
                      } else {
                        const n = Number(part);
                        if (n >= 1) pages.add(n);
                      }
                    });
                    selected = pdfImages.filter((_, idx) => pages.has(idx+1));
                  }
                  for (const img of selected) {
                    const a = document.createElement('a');
                    a.href = img.url;
                    a.download = img.name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }
                  setPdf2imgDownloadStatus('Images downloaded successfully.');
                }}
              >
                Download Selected Images
              </button>
                          {pdf2imgDownloadStatus && <div className="tool2page-status tool2page-status-success" style={{marginTop:8}}>{pdf2imgDownloadStatus}</div>}
            </div>
          )}
        </div>

        <div className="tool2page-section tool2page-toolbox">
          <h3>Compress PDF (Reduce File Size)</h3>
          <label className="tool2page-input-label">
            <span className="tool2page-file-btn">Choose PDF
              <input type="file" accept="application/pdf" onChange={e => { setCompressPdfFile(e.target.files[0]); setCompressStatus(''); }} className="tool2page-input" />
            </span>
            <span className="tool2page-file-name">{compressPdfFile ? compressPdfFile.name : 'No file chosen'}</span>
          </label>
          <label style={{ fontWeight: 500, color: darkMode ? '#60a5fa' : '#2355d6', marginBottom: 12 }}>
            PDF Size: &nbsp;
            <input type="range" min={10} max={100} value={compressQuality} onChange={async e => {
              const val = Number(e.target.value);
              setCompressQuality(val);
              // Estimate compressed size if file is selected
              if (compressPdfFile) {
                setCompressEstimate('Calculating...');
                try {
                  const arr = await compressPdfFile.arrayBuffer();
                  const pdf = await pdfjsLib.getDocument({ data: arr }).promise;
                  const page = await pdf.getPage(1);
                  const viewport = page.getViewport({ scale: 1.5 });
                  const canvas = document.createElement('canvas');
                  const context = canvas.getContext('2d');
                  canvas.width = viewport.width;
                  canvas.height = viewport.height;
                  await page.render({ canvasContext: context, viewport }).promise;
                  const dataUrl = canvas.toDataURL('image/jpeg', val / 100);
                  const imgBytes = await fetch(dataUrl).then(res => res.arrayBuffer());
                  // Estimate: (compressed first page size) * numPages
                  const estSize = (imgBytes.byteLength * pdf.numPages);
                  let size = estSize / 1024;
                  let unit = 'KB';
                  if (size > 1024) {
                    size = size / 1024;
                    unit = 'MB';
                  }
                  setCompressEstimate({ size: size.toFixed(1), unit });
                } catch {
                  setCompressEstimate(null);
                }
              } else {
                setCompressEstimate(null);
              }
            }} style={{ verticalAlign: 'middle' }} />
            <span style={{ marginLeft: 8 }}>{compressQuality}%</span>
          </label>
          {compressEstimate && (
            <div style={{ color: '#888', fontSize: 14, marginBottom: 8 }}>
              Estimated size: {typeof compressEstimate === 'string' ? compressEstimate : `${compressEstimate.size} ${compressEstimate.unit}`}
            </div>
          )}<br></br>
          <button className="tool2page-main-btn" disabled={!compressPdfFile} onClick={async () => {
            setCompressEstimate('Calculating...');
            if (!compressPdfFile) return;
            setCompressStatus('Compressing...');
            try {
              // Step 1: Read PDF and render each page as image
              const arr = await compressPdfFile.arrayBuffer();
              const pdf = await pdfjsLib.getDocument({ data: arr }).promise;
              const pdfDoc = await PDFDocument.create();
              for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const viewport = page.getViewport({ scale: 1.5 });
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                await page.render({ canvasContext: context, viewport }).promise;
                // Step 2: Compress image using JPEG quality
                const dataUrl = canvas.toDataURL('image/jpeg', compressQuality / 100);
                const imgBytes = await fetch(dataUrl).then(res => res.arrayBuffer());
                const pdfImg = await pdfDoc.embedJpg(imgBytes);
                const dims = pdfImg.scale(1);
                const newPage = pdfDoc.addPage([dims.width, dims.height]);
                newPage.drawImage(pdfImg, { x: 0, y: 0, width: dims.width, height: dims.height });
              }
              const outBytes = await pdfDoc.save();
              // After compression, show actual size
              let outSize = outBytes.byteLength / 1024;
              let outUnit = 'KB';
              if (outSize > 1024) {
                outSize = outSize / 1024;
                outUnit = 'MB';
              }
              setCompressEstimate({ size: outSize.toFixed(1), unit: outUnit });
              saveAs(new Blob([outBytes], { type: 'application/pdf' }), 'compressed.pdf');
              setCompressStatus('Compressed PDF downloaded successfully.');
            } catch (err) {
              setCompressStatus('Compression failed. ' + (err.message || ''));
            }
          }}>Compress & Download PDF</button>
                    {compressStatus && <div className="tool2page-status tool2page-status-success">{compressStatus}</div>}
          <div style={{marginTop:10, color:'#888', fontSize:13}}>
            Note: This method rasterizes each page as an image. Text will not be selectable in the compressed PDF.<br/>
            For best results, use with image-heavy PDFs.
          </div>
        </div>

        <div className="tool2page-section tool2page-toolbox">
          <h3>Word to PDF</h3>
          <label className="tool2page-input-label">
            <span className="tool2page-file-btn">Choose Word
              <input type="file" accept=".docx" onChange={handleWordFile} className="tool2page-input" />
            </span>
            <span className="tool2page-file-name">{wordFile ? wordFile.name : 'No file chosen'}</span>
          </label>
          <button className="tool2page-main-btn" disabled={!wordFile} onClick={handleWordToPdf}>Convert & Download PDF</button>
                  {wordToPdfStatus && <div className="tool2page-status tool2page-status-success">{wordToPdfStatus}</div>}
        </div>


        <div className="tool2page-section tool2page-toolbox">
          <h3>PDF to Word</h3>
          <label className="tool2page-input-label">
            <span className="tool2page-file-btn">Choose PDF
              <input type="file" accept="application/pdf" onChange={handlePdfToWordFile} className="tool2page-input" />
            </span>
            <span className="tool2page-file-name">{pdfToWordFile ? pdfToWordFile.name : 'No file chosen'}</span>
          </label>
          <button className="tool2page-main-btn" disabled={!pdfToWordFile} onClick={handlePdfToWord}>Convert & Download Word</button>
          {pdfToWordStatus && <div className="tool2page-status tool2page-status-success">{pdfToWordStatus}</div>}
        </div>

        {/* Merge PDFs Section */}
        <div className="tool2page-section tool2page-toolbox">
          <h3>Merge PDFs</h3>
          <label className="tool2page-input-label">
            <span className="tool2page-file-btn">Choose PDFs
              <input type="file" accept="application/pdf" multiple onChange={handleMergeFiles} className="tool2page-input" />
            </span>
            <span className="tool2page-file-name">{mergeFiles.length > 0 ? mergeFiles.map(f => f.name).join(', ') : 'No files chosen'}</span>
          </label>
          <button className="tool2page-main-btn" disabled={mergeFiles.length < 2} onClick={handleMergePdfs}>Merge & Download PDF</button>
          {mergeStatus && <div className="tool2page-status tool2page-status-success">{mergeStatus}</div>}
          <div style={{marginTop:10, color:'#888', fontSize:13}}>
            Select two or more PDF files to merge them into a single PDF. All processing is done in your browser.
          </div>
        </div>

        <div className="tool2page-section tool2page-toolbox">
          <h3>PDF Type Conversion</h3>
          <label className="tool2page-input-label">
            <span className="tool2page-file-btn">Choose PDF
              <input type="file" accept="application/pdf" onChange={handlePdfConvFile} className="tool2page-input" />
            </span>
            <span className="tool2page-file-name">{pdfConvFile ? pdfConvFile.name : 'No file chosen'}</span>
          </label>
          <select value={convType} onChange={e => setConvType(e.target.value)} className="tool2page-select">
            <option value="pdfa">PDF/A</option>
            <option value="pdfx">PDF/X</option>
            <option value="pdf">Standard PDF</option>
          </select>
          <button className="tool2page-main-btn" disabled={!pdfConvFile} onClick={handlePdfTypeConvert}>Convert & Download</button>
                  {convStatus && <div className="tool2page-status tool2page-status-success">{convStatus}</div>}
        </div>
      </div>
      {/* Responsive styles + toolbox section style */}
      <style>{`
        .tool2page-status {
          margin-top: 10px;
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 15px;
          font-weight: 500;
          box-shadow: 0 1px 4px rgba(34,197,94,0.08);
          display: inline-block;
          min-width: 120px;
          max-width: 90%;
          word-break: break-word;
        }
        .tool2page-status-success {
          background: #e6f9ed;
          color: #15803d;
          border: 1.5px solid #22c55e;
          margin-top: 18px !important;
          margin-left: 25px !important;
        }
        .tool2page-dark .tool2page-status-success {
          background: #1e2e1e;
          color: #22c55e;
          border-color: #15803d;
        }
        .tool2page-toolbox {
          border: 2px dashed #bfc7d1;
          border-radius: 12px;
          margin-bottom: 32px;
          padding: 24px 12px;
          // background: #fff;
          width: 90%;
          max-width: 900px;
          display: block;
          box-sizing: border-box;
        }
        .tool2page-dark .tool2page-toolbox {
          border-color: #334155;
          // background: #1a1a2c;
        }
        .tool2page-main-btn {
          background: #9A3B3B !important;
          color: #fff;
          border: none;
          border-radius: 7px;
          padding: 7px 18px;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(154,59,59,0.08);
          margin-top: 10px;
          margin-bottom: 8px;
          min-width: 160px;
        }
        .tool2page-main-btn:hover {
          background: #7a2d2d !important;
          color: #fff;
        }
        .tool2page-input-label {
          position: relative;
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 14px;
          margin-bottom: 18px;
        }
        .tool2page-file-btn {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          background: #9A3B3B;
          color: #fff;
          border: none;
          border-radius: 6px;
          padding: 7px 18px 7px 14px;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          transition: background 0.2s, box-shadow 0.2s;
          position: relative;
          z-index: 1;
          box-shadow: 0 2px 8px rgba(154,59,59,0.08);
        }
        .tool2page-file-btn:focus-within, .tool2page-file-btn:active {
          outline: 2px solid #2355d6;
          box-shadow: 0 0 0 3px #eaf1ff;
        }
        .tool2page-file-btn svg {
          width: 18px;
          height: 18px;
          fill: currentColor;
        }
        .tool2page-file-name {
          font-size: 14px;
          color: #444;
          margin-left: 8px;
          max-width: 320px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          background: #f5f6fa;
          border-radius: 5px;
          padding: 5px 10px;
          border: 1px solid #e0e0e0;
        }
        .tool2page-dark .tool2page-file-btn {
          background: #9A3B3B;
          color: #fff;
        }
        .tool2page-dark .tool2page-file-btn:focus-within, .tool2page-dark .tool2page-file-btn:active {
          outline: 2px solid #60a5fa;
          box-shadow: 0 0 0 3px #23234a;
        }
        .tool2page-dark .tool2page-file-name {
          color: #eee;
          background: #23234a;
          border: 1px solid #334155;
        }
        @media (max-width: 600px) {
          .tool2page-file-name {
            max-width: 120px;
            font-size: 12px;
            padding: 4px 6px;
          }
          .tool2page-file-btn {
            font-size: 13px;
            padding: 6px 10px 6px 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default Tool2Page;
